import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type GuardOptions = {
  enabled: boolean;
  beforeUnload?: boolean; // default true
};

export const useUnsavedChangesGuard = (options: GuardOptions) => {
  const { enabled, beforeUnload = true } = options;
  const navigate = useNavigate();
  const location = useLocation();

  const nextPathRef = useRef<string | null>(null);
  const nextActionRef = useRef<(() => void) | null>(null);
  const [shouldOpenModal, setShouldOpenModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const isRevertingPopRef = useRef(false);

  // 拦截浏览器刷新/关闭
  useEffect(() => {
    if (!enabled || !beforeUnload) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // 某些浏览器需要设置 returnValue 以触发提示
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [enabled, beforeUnload]);

  // API：尝试跳转时调用（用于显式 Back 按钮等）
  const confirmNavigate = useCallback(
    (to: string | (() => void)) => {
      if (!enabled) {
        if (typeof to === 'function') return to();
        navigate(to);
        return;
      }
      if (typeof to === 'function') {
        nextActionRef.current = to;
        nextPathRef.current = null;
      } else {
        nextPathRef.current = to;
        nextActionRef.current = null;
      }
      setShouldOpenModal(true);
    },
    [enabled, navigate]
  );

  // 拦截站内链接点击（<a> 标签）
  useEffect(() => {
    if (!enabled) return;
    const onClick = (e: MouseEvent) => {
      if (!enabled) return;
      // 只处理主键点击、未按修饰键
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      )
        return;
      const target = e.target as HTMLElement | null;
      const anchor =
        target?.closest &&
        (target.closest('a[href]') as HTMLAnchorElement | null);
      if (!anchor) return;
      if (anchor.target && anchor.target !== '_self') return; // 新窗口不拦
      const href = anchor.getAttribute('href') || '';
      if (!href || href.startsWith('#')) return;
      // 仅拦截同源链接
      const url = href.startsWith('http')
        ? new URL(href)
        : new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      // 跳过上传相关路径（不弹窗）
      if (url.pathname.startsWith('/upload')) return;
      e.preventDefault();
      nextPathRef.current = url.pathname + url.search + url.hash;
      nextActionRef.current = null;
      setShouldOpenModal(true);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [enabled]);

  // 拦截浏览器后退/前进
  useEffect(() => {
    if (!enabled) return;
    const onPopState = () => {
      if (!enabled) return;
      if (isRevertingPopRef.current) return;
      // 立即恢复到当前页，并记录一个回退动作
      isRevertingPopRef.current = true;
      window.history.forward();
      setTimeout(() => {
        isRevertingPopRef.current = false;
      }, 0);
      nextPathRef.current = null;
      nextActionRef.current = () => window.history.back();
      setShouldOpenModal(true);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [enabled]);

  // 保存后放行
  const proceed = useCallback(() => {
    const target = nextPathRef.current;
    const action = nextActionRef.current;
    nextPathRef.current = null;
    nextActionRef.current = null;
    if (action) action();
    else if (target) navigate(target);
    setShouldOpenModal(false);
    setBusy(false);
  }, [navigate]);

  // 丢弃后放行
  const discard = useCallback(() => {
    const target = nextPathRef.current;
    const action = nextActionRef.current;
    nextPathRef.current = null;
    nextActionRef.current = null;
    if (action) action();
    else if (target) navigate(target);
    setShouldOpenModal(false);
  }, [navigate]);

  return {
    location,
    confirmNavigate,
    shouldOpenModal,
    setShouldOpenModal,
    busy,
    setBusy,
    proceed,
    discard,
  };
};

// 在 EditProfile 中注册全局拦截器，供全站 programmatic navigate 使用
export const registerGlobalUnsavedGuard = (options: {
  enabled: boolean;
  openPrompt: (attempt: () => void) => void;
}) => {
  const guard = {
    request: (action: () => void) => {
      if (options.enabled) {
        options.openPrompt(action);
      } else {
        action();
      }
    },
  };
  // @ts-ignore
  (window as any).__TAG_NAV_GUARD__ = guard;
  return () => {
    // @ts-ignore
    if ((window as any).__TAG_NAV_GUARD__) {
      // @ts-ignore
      delete (window as any).__TAG_NAV_GUARD__;
    }
  };
};
